/**
 * The crystal resonator: a slim tower on its ore, futuristic and simple. A dark plinth, three light
 * struts round a core that fills with violet light as it stores crystal, a cap with a glowing tip,
 * a broken ring that turns while it draws, and crystals leaning at its foot so the ore beneath is
 * plain. It stands well inside its tile: the purple ground shows all round. Offsets are in
 * resonator space, y up from the tile's top, a tile about 1.2 wide.
 */
import * as THREE from 'three';

import { around, part, type MachinePart } from './part';

const SLATE = 0x3b4150;
const METAL = 0xd9dee8;
const VIOLET = 0xb69cff;
const SHARD = 0x9e7fe0;
const GLOW = 0x6b3fd6;
const RING = 0x5fe0c8;
const RING_GLOW = 0x2fb8a0;

/** The core, empty to full: its height at full, from the plinth up. */
export const CORE_FULL = 0.44;
const PLINTH = 0.08;

const THIRDS = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3] as const;

/** The core, a unit tall from its foot: the props scale it to how full the resonator is. */
export const RESONATOR_CORE: MachinePart = part(
  'res.core',
  new THREE.CylinderGeometry(0.075, 0.075, 1, 12).translate(0, 0.5, 0),
  VIOLET,
  [0, PLINTH, 0],
  { roughness: 0.3, glow: GLOW },
);

/** Three arcs round the struts: turning, they show the resonator at work. */
export const RESONATOR_RING: MachinePart = part(
  'res.ring',
  around(() => new THREE.TorusGeometry(0.2, 0.016, 6, 14, 1.5).rotateX(Math.PI / 2), THIRDS, 0),
  RING,
  [0, 0.33, 0],
  { roughness: 0.35, spin: 'y', speed: 1.1, glow: RING_GLOW },
);

/** The parts that stand still. */
export const RESONATOR_FRAME: readonly MachinePart[] = [
  part('res.base', new THREE.CylinderGeometry(0.2, 0.24, PLINTH, 6), SLATE, [0, PLINTH / 2, 0], {
    metalness: 0.35,
  }),
  part(
    'res.struts',
    around(() => new THREE.CylinderGeometry(0.022, 0.022, CORE_FULL, 6), THIRDS, 0.13),
    METAL,
    [0, PLINTH + CORE_FULL / 2, 0],
    { metalness: 0.4 },
  ),
  part(
    'res.cap',
    new THREE.CylinderGeometry(0.15, 0.17, 0.07, 12),
    METAL,
    [0, PLINTH + CORE_FULL + 0.035, 0],
    { metalness: 0.4 },
  ),
  part('res.tip', new THREE.OctahedronGeometry(0.055), VIOLET, [0, PLINTH + CORE_FULL + 0.13, 0], {
    roughness: 0.3,
    glow: GLOW,
  }),
  part(
    'res.shards',
    around(
      () => new THREE.OctahedronGeometry(0.075).scale(0.7, 1.9, 0.7).rotateZ(0.4),
      [0.5, 2.6, 4.4],
      0.3,
    ),
    SHARD,
    [0, 0.09, 0],
    { roughness: 0.35, glow: 0x9d6bff, pulse: true },
  ),
];

export const RESONATOR_PARTS: readonly MachinePart[] = [
  ...RESONATOR_FRAME,
  RESONATOR_CORE,
  RESONATOR_RING,
];
